/**
 * Encryption Service for LucidDock
 *
 * Provides AES-256-GCM encryption for sensitive fields using Web Crypto API.
 * This is a client-side encryption service that encrypts data before storing in Firestore.
 *
 * Security Model:
 * - Uses AES-256-GCM (authenticated encryption)
 * - Derives unique encryption key per user using PBKDF2
 * - Stores encrypted data with IV prepended for decryption
 * - Never stores or transmits plaintext sensitive data
 *
 * Usage:
 *   const encrypted = await encryptionService.encrypt('sensitive-value', userId);
 *   const decrypted = await encryptionService.decrypt(encrypted, userId);
 */

// App-level salt for key derivation (not secret, but unique to app)
const APP_SALT = 'LucidDock-Encryption-Salt-v1';

// Encryption configuration
const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96 bits for GCM
const PBKDF2_ITERATIONS = 100000;

/**
 * Encrypted value format:
 * Base64(IV + Ciphertext + AuthTag)
 */

class EncryptionService {
  private keyCache: Map<string, { key: CryptoKey; cachedAt: number }> = new Map();
  private static KEY_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

  /**
   * Derives a unique encryption key for a user using PBKDF2
   */
  private async deriveKey(userId: string): Promise<CryptoKey> {
    // Check cache first (with TTL)
    const cached = this.keyCache.get(userId);
    if (cached && Date.now() - cached.cachedAt < EncryptionService.KEY_TTL_MS) {
      return cached.key;
    }

    // Create a consistent seed from userId + app salt
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(userId + APP_SALT),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    // Derive the actual encryption key
    const salt = encoder.encode(APP_SALT + '-key-derivation');
    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: ALGORITHM, length: KEY_LENGTH },
      false,
      ['encrypt', 'decrypt']
    );

    // Cache the key with timestamp for TTL expiration
    this.keyCache.set(userId, { key, cachedAt: Date.now() });
    return key;
  }

  /**
   * Encrypts a plaintext value
   * @param plainText - The value to encrypt
   * @param userId - User ID for key derivation
   * @returns Encrypted value as base64 string, or empty string if input is empty
   */
  async encrypt(plainText: string, userId: string): Promise<string> {
    if (!plainText) {
      return '';
    }
    // If no userId, return original value (can't encrypt without key)
    if (!userId) {
      console.warn('Encryption skipped: No userId provided');
      return plainText;
    }

    try {
      const key = await this.deriveKey(userId);
      const encoder = new TextEncoder();
      const data = encoder.encode(plainText);

      // Generate random IV for each encryption
      const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

      // Encrypt the data
      const encrypted = await crypto.subtle.encrypt(
        { name: ALGORITHM, iv },
        key,
        data
      );

      // Combine IV + encrypted data
      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      // Return as base64
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypts an encrypted value
   * @param encryptedData - Base64 encoded encrypted value
   * @param userId - User ID for key derivation
   * @returns Decrypted plaintext, or original value if decryption fails (backward compatibility with plaintext data)
   */
  async decrypt(encryptedData: string, userId: string): Promise<string> {
    if (!encryptedData || !userId) {
      return '';
    }

    try {
      const key = await this.deriveKey(userId);

      // Decode from base64
      const combined = new Uint8Array(
        atob(encryptedData).split('').map(c => c.charCodeAt(0))
      );

      // Extract IV and encrypted data
      const iv = combined.slice(0, IV_LENGTH);
      const data = combined.slice(IV_LENGTH);

      // Decrypt the data
      const decrypted = await crypto.subtle.decrypt(
        { name: ALGORITHM, iv },
        key,
        data
      );

      // Convert back to string
      const decoder = new TextDecoder();
      return decoder.decode(decrypted);
    } catch {
      // Decryption failed - this means data is either:
      // 1. Plaintext (not encrypted yet) - return as-is for backward compatibility
      // 2. Corrupted or encrypted with different key - return as-is and log
      // This handles migration from unencrypted to encrypted data
      return encryptedData;
    }
  }

  /**
   * Decrypts an encrypted value, trying multiple keys if needed
   * @param encryptedData - Base64 encoded encrypted value
   * @param primaryUserId - Primary user ID for key derivation (usually profile owner)
   * @param fallbackUserId - Fallback user ID if primary fails (usually current user)
   * @returns Decrypted plaintext, or original value if not encrypted (backward compatibility)
   */
  async decryptWithFallback(encryptedData: string, primaryUserId: string, fallbackUserId?: string): Promise<string> {
    if (!encryptedData) {
      return '';
    }

    // Try primary key first
    if (primaryUserId) {
      try {
        const key = await this.deriveKey(primaryUserId);
        const combined = new Uint8Array(
          atob(encryptedData).split('').map(c => c.charCodeAt(0))
        );
        const iv = combined.slice(0, IV_LENGTH);
        const data = combined.slice(IV_LENGTH);
        const decrypted = await crypto.subtle.decrypt(
          { name: ALGORITHM, iv },
          key,
          data
        );
        const decoder = new TextDecoder();
        return decoder.decode(decrypted);
      } catch {
        // Primary key failed, try fallback
      }
    }

    // Try fallback key if different from primary and primary failed
    if (fallbackUserId && fallbackUserId !== primaryUserId) {
      try {
        const key = await this.deriveKey(fallbackUserId);
        const combined = new Uint8Array(
          atob(encryptedData).split('').map(c => c.charCodeAt(0))
        );
        const iv = combined.slice(0, IV_LENGTH);
        const data = combined.slice(IV_LENGTH);
        const decrypted = await crypto.subtle.decrypt(
          { name: ALGORITHM, iv },
          key,
          data
        );
        const decoder = new TextDecoder();
        return decoder.decode(decrypted);
      } catch {
        // Both keys failed
      }
    }

    // Both decryption attempts failed - this means data is either:
    // 1. Plaintext (not encrypted yet) - return as-is for backward compatibility
    // 2. Corrupted - return as-is (better to show original than empty)
    // This approach handles migration from unencrypted to encrypted data
    return encryptedData;
  }

  /**
   * Checks if a value appears to be encrypted (base64 with correct format)
   * @param value - The value to check
   * @returns True if the value looks like encrypted data
   *
   * AES-256-GCM encrypted values contain:
   * - 12 bytes IV
   * - At least 1 byte ciphertext
   * - 16 bytes auth tag
   * = Minimum 29 bytes binary → ~40 chars base64
   */
  isEncrypted(value: string): boolean {
    // Check if value looks like it could be encrypted (base64 encoded binary data)
    //
    // AES-256-GCM encrypted values contain:
    // - 12 bytes IV (always 12)
    // - N bytes ciphertext (same length as plaintext)
    // - 16 bytes auth tag (always 16)
    // = Minimum 29 bytes binary for 1-char input → ~39 chars base64
    //
    // Strategy: Use multiple heuristics to distinguish encrypted data from plain text
    // IMPORTANT: We err on the side of treating ambiguous values as plain text
    // to avoid accidentally clearing user's actual passwords

    if (!value) {
      return false;
    }

    // 1. Length check: encrypted data for even 1 char is ~39+ chars base64
    //    Be strict here - most passwords are under 40 chars
    if (value.length < 39) {
      return false;
    }

    // 2. Character set check: base64 only uses A-Z, a-z, 0-9, +, /, =
    //    Any other character means it's definitely plain text
    if (/[^A-Za-z0-9+/=]/.test(value)) {
      return false;
    }

    // 3. Base64 padding check: valid base64 has specific padding rules
    //    - Length must be divisible by 4
    //    - Padding (=) only at end, max 2
    //    - No = in the middle
    const paddingMatch = value.match(/=+$/);
    const paddingLength = paddingMatch ? paddingMatch[0].length : 0;
    if (paddingLength > 2) {
      return false;
    }
    if (value.includes('=') && !value.endsWith('=')) {
      return false; // = in middle of string
    }
    if ((value.length - paddingLength) % 4 !== 0 && value.length % 4 !== 0) {
      // Not valid base64 length (though browsers are lenient)
    }

    // 4. Common password word detection (case-insensitive)
    const lowerValue = value.toLowerCase();
    const commonWords = [
      'password', 'passwd', 'pass', 'wifi', 'code', 'key', 'secret',
      'admin', 'user', 'guest', 'home', 'network', 'router', 'secure',
      'login', 'access', 'enter', 'welcome', 'hello', 'test', 'temp',
      'house', 'family', 'love', 'qwerty', 'letmein', 'master', 'dragon',
      'baseball', 'football', 'monkey', 'shadow', 'sunshine', 'princess',
      'michael', 'jennifer', 'thomas', 'charlie', 'andrew', 'joshua',
    ];
    for (const word of commonWords) {
      if (lowerValue.includes(word)) {
        return false;
      }
    }

    // 5. Pattern detection for common password structures
    const plainTextPatterns = [
      /^[a-z]+$/i,                    // All letters only (mypassword)
      /^[0-9]+$/,                     // All digits only (123456789...)
      /^[a-z]+[0-9]+$/i,              // Letters then digits (password123)
      /^[0-9]+[a-z]+$/i,              // Digits then letters (123password)
      /^[a-z]+[0-9]+[a-z]+$/i,        // Letters-digits-letters (pass123word)
      /^[A-Z][a-z]+[0-9]+$/,          // Capitalized word + digits (Password123)
      /^[A-Z][a-z]+[A-Z][a-z]+/,      // CamelCase (MySecretPassword)
      /(.)\1{3,}/,                    // 4+ repeated chars (aaaa, 1111)
      /^[A-Z]+$/,                     // All uppercase (MYPASSWORD)
      /^[a-z]+$/,                     // All lowercase (mypassword)
      /^[A-Za-z]+[0-9]{1,4}$/,        // Word + short number (password24)
      /^[0-9]{1,4}[A-Za-z]+$/,        // Short number + word (2024password)
      /[A-Z][a-z]+[A-Z][a-z]+[0-9]*/, // MultiWordCamelCase123
    ];

    for (const pattern of plainTextPatterns) {
      if (pattern.test(value)) {
        return false;
      }
    }

    // 6. Check for readable English-like patterns
    //    Encrypted data decoded as text looks like garbage
    //    Real passwords often have vowel-consonant patterns
    const vowelConsonantRatio = (value.match(/[aeiou]/gi) || []).length / value.length;
    // English text typically has 35-45% vowels, encrypted data has random distribution
    if (vowelConsonantRatio > 0.25 && vowelConsonantRatio < 0.50) {
      // Looks like readable text, probably a password
      return false;
    }

    // 7. Base64 structure validation
    try {
      const decoded = atob(value);

      // Encrypted data must have at least: IV (12) + ciphertext (1) + auth tag (16) = 29 bytes
      if (decoded.length < 29) {
        return false;
      }

      // 8. Entropy check on decoded binary data
      //    Encrypted data should have high entropy (many unique byte values)
      const bytes = decoded.split('').map(c => c.charCodeAt(0));
      const uniqueBytes = new Set(bytes).size;
      const entropyRatio = uniqueBytes / decoded.length;

      // Encrypted data should have high entropy (> 0.6 for data of this size)
      // Plain text passwords have lower entropy
      if (entropyRatio < 0.5) {
        return false;
      }

      // 9. Check for printable ASCII in decoded data
      //    Plain text base64-encoded would decode to readable chars
      //    Encrypted data decodes to binary garbage (non-printable chars)
      const printableChars = bytes.filter(b => b >= 32 && b <= 126).length;
      const printableRatio = printableChars / decoded.length;

      // If most decoded chars are printable, it might be base64-encoded plain text
      // Encrypted data has lots of non-printable bytes
      if (printableRatio > 0.7) {
        return false;
      }

      // 10. Final sanity check: the decoded length should make sense for encryption
      //     For AES-GCM: decoded = IV (12) + ciphertext (N) + tag (16)
      //     So: original_length = decoded_length - 28
      const estimatedOriginalLength = decoded.length - 28;
      // If original would be > 1000 chars, suspicious (most passwords are short)
      if (estimatedOriginalLength > 500) {
        return false;
      }

      return true;
    } catch {
      // Failed to decode as base64 - definitely not encrypted
      return false;
    }
  }

  /**
   * Masks a value for display (shows last N characters)
   * @param value - The value to mask
   * @param visibleChars - Number of characters to show at end (default 4)
   * @returns Masked string like "••••••7890"
   */
  maskValue(value: string, visibleChars: number = 4): string {
    if (!value) return '';
    if (value.length <= visibleChars) return value;

    const visible = value.slice(-visibleChars);
    const maskedLength = Math.min(value.length - visibleChars, 8); // Cap mask length
    const masked = '•'.repeat(maskedLength);
    return masked + visible;
  }

  /**
   * Masks a credit card number (shows first 4 and last 4)
   * @param value - The card number
   * @returns Masked string like "4532 •••• •••• 7890"
   */
  maskCardNumber(value: string): string {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length < 8) return this.maskValue(value);

    const first4 = digits.slice(0, 4);
    const last4 = digits.slice(-4);
    return `${first4} •••• •••• ${last4}`;
  }

  /**
   * Masks a routing number (shows last 4)
   * @param value - The routing number
   * @returns Masked string like "•••••4321"
   */
  maskRoutingNumber(value: string): string {
    return this.maskValue(value, 4);
  }

  /**
   * Masks an account number (shows last 4)
   * @param value - The account number
   * @returns Masked string like "••••••7890"
   */
  maskAccountNumber(value: string): string {
    return this.maskValue(value, 4);
  }

  /**
   * Masks a VIN (shows last 6)
   * @param value - The VIN
   * @returns Masked string like "•••••••••••123456"
   */
  maskVIN(value: string): string {
    return this.maskValue(value, 6);
  }

  /**
   * Masks a policy/member ID (shows last 4)
   * @param value - The ID
   * @returns Masked string like "••••••1234"
   */
  maskPolicyNumber(value: string): string {
    return this.maskValue(value, 4);
  }

  /**
   * Clears the key cache (call on logout)
   */
  clearCache(): void {
    this.keyCache.clear();
  }

  /**
   * Creates a deterministic hash for queryable lookup of sensitive data
   * Uses SHA-256 with app salt for consistent results
   * @param value - The value to hash
   * @returns Hex-encoded hash string
   */
  async hash(value: string): Promise<string> {
    if (!value) return '';

    const encoder = new TextEncoder();
    // Combine value with app salt for additional security
    const data = encoder.encode(value + APP_SALT);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = new Uint8Array(hashBuffer);
    // Convert to hex string
    return Array.from(hashArray)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }
}

// Export singleton instance
export const encryptionService = new EncryptionService();

// Export types for use in components
export type EncryptedField = string;

// Field type identifiers for proper masking
export type SecureFieldType =
  | 'account_number'
  | 'routing_number'
  | 'credit_card'
  | 'vin'
  | 'policy_number'
  | 'member_id'
  | 'ssn'
  | 'password'
  | 'access_code'
  | 'generic';

/**
 * Gets the appropriate mask function for a field type
 */
export function getMaskFunction(fieldType: SecureFieldType): (value: string) => string {
  switch (fieldType) {
    case 'credit_card':
      return (v) => encryptionService.maskCardNumber(v);
    case 'vin':
      return (v) => encryptionService.maskVIN(v);
    case 'account_number':
      return (v) => encryptionService.maskAccountNumber(v);
    case 'routing_number':
      return (v) => encryptionService.maskRoutingNumber(v);
    case 'policy_number':
    case 'member_id':
      return (v) => encryptionService.maskPolicyNumber(v);
    case 'ssn':
      return (v) => encryptionService.maskValue(v, 4);
    case 'password':
    case 'access_code':
      return (v) => '•'.repeat(Math.min(v.length, 12));
    default:
      return (v) => encryptionService.maskValue(v, 4);
  }
}
